import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CourseProvider } from './contexts/CourseContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Footer from './components/common/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import StudentPortalLayout from './components/layout/StudentPortalLayout';

// Pages
import Home from './pages/Home';
import ForgotPassword from './pages/ForgotPassword';
import PortalMyLearning from './pages/student-portal/PortalMyLearning';
import PortalCourses from './pages/student-portal/PortalCourses';
import PortalWallet from './pages/student-portal/PortalWallet';
import PortalForum from './pages/student-portal/PortalForum';

// Components
import Navbar from './components/common/Navbar';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ResetPassword from './components/auth/ResetPassword';
import Profile from './components/user/Profile';
import CourseDetail from './components/course/CourseDetail';
import CourseViewer from './components/course/CourseViewer';
import RefreshTokenTest from './components/test/RefreshTokenTest';
import UnitViewer from './components/unit/UnitViewer';
import GradeCoursesPage from './pages/GradeCoursesPage';
import ParentMonitoring from './pages/ParentMonitoring';

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <CourseProvider>
              <ScrollToTop />
              <div className="min-h-screen bg-slate-50">
                <Routes>
                  {/* Public routes with Navbar and Footer */}
                  <Route path="/" element={
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <Home />
                      </main>
                      <Footer />
                    </>
                  } />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <ForgotPassword />
                      </main>
                      <Footer />
                    </>
                  } />
                  <Route path="/reset-password" element={
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <ResetPassword />
                      </main>
                      <Footer />
                    </>
                  } />
                  <Route path="/parent-monitoring" element={
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <ParentMonitoring />
                      </main>
                      <Footer />
                    </>
                  } />

                  {/* Grade Courses Routes */}
                  <Route path="/courses/grade/:grade" element={
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <GradeCoursesPage />
                      </main>
                      <Footer />
                    </>
                  } />
                  
                  <Route path="/courses/:id" element={
                    <>
                      <Navbar />
                      <main className="flex-grow">
                        <CourseDetail />
                      </main>
                      <Footer />
                    </>
                  } />
                  
                  {/* Protected routes with Navbar */}
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <Navbar />
                      <main className="flex-grow">
                        <Profile />
                      </main>
                      <Footer />
                    </ProtectedRoute>
                  } />

                  {/* Redirects to portal */}
                  <Route path="/enrolled-courses" element={<Navigate to="/student-portal" replace />} />
                  <Route path="/my-lectures" element={<Navigate to="/student-portal" replace />} />

                  {/* Student Portal Routes (no Navbar/Footer) */}
                  <Route path="/student-portal" element={
                    <ProtectedRoute>
                      <StudentPortalLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<PortalMyLearning />} />
                    <Route path="courses" element={<PortalCourses />} />
                    <Route path="wallet" element={<PortalWallet />} />
                    <Route path="forum" element={<PortalForum />} />
                  </Route>

                  {/* Course and Unit Viewers (no Navbar/Footer) */}
                  <Route path="/course-player/:courseId" element={
                    <ProtectedRoute>
                      <CourseViewer />
                    </ProtectedRoute>
                  } />
                  <Route path="/unit-viewer/:id" element={
                    <ProtectedRoute>
                      <UnitViewer />
                    </ProtectedRoute>
                  } />

                  {/* Test Route */}
                  <Route path="/test-refresh-token" element={
                    <ProtectedRoute>
                      <Navbar />
                      <main className="flex-grow">
                        <RefreshTokenTest />
                      </main>
                      <Footer />
                    </ProtectedRoute>
                  } />
                </Routes>
              </div>
            </CourseProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
